import { tasksAPI } from '../../services/api';
import { showNotification } from './uiActions';
import { getUserInfo } from './authActions';
import { safeDecodeFileName } from '../../utils/fileNameUtils';

// 定义动作类型
export const FETCH_TASKS_START = 'FETCH_TASKS_START';
export const FETCH_TASKS_SUCCESS = 'FETCH_TASKS_SUCCESS';
export const FETCH_TASKS_FAIL = 'FETCH_TASKS_FAIL';
export const UPLOAD_TASK_START = 'UPLOAD_TASK_START';
export const UPLOAD_TASK_SUCCESS = 'UPLOAD_TASK_SUCCESS';
export const UPLOAD_TASK_FAIL = 'UPLOAD_TASK_FAIL';
export const CANCEL_TASK_SUCCESS = 'CANCEL_TASK_SUCCESS';
export const UPDATE_TASK_DETAILS = 'UPDATE_TASK_DETAILS';
export const DELETE_TASK_FILES_START = 'DELETE_TASK_FILES_START';
export const DELETE_TASK_FILES_SUCCESS = 'DELETE_TASK_FILES_SUCCESS';
export const DELETE_TASK_FILES_FAIL = 'DELETE_TASK_FILES_FAIL';

// 存储轮询任务的interval ID
let pollInterval = null;

// 获取任务列表
export const fetchTasks = (params = {}) => {
  return async (dispatch) => {
    dispatch({ type: FETCH_TASKS_START });
    
    try {
      const response = await tasksAPI.getTasks(params);
      
      // 添加调试日志，查看返回的任务数据
      //console.log('获取到的任务列表:', response.data);
      
      dispatch({
        type: FETCH_TASKS_SUCCESS,
        payload: response.data
      });
      
      return response;
    } catch (error) {
      dispatch({
        type: FETCH_TASKS_FAIL,
        payload: error.response?.data?.message || '获取任务列表失败'
      });
      
      throw error;
    }
  };
};

// 上传文件创建翻译任务
export const uploadFiles = (files, language, certification_type = 'none', is_urgent = false) => {
  return async (dispatch) => {
    dispatch({ type: UPLOAD_TASK_START });
    try {
      const response = await tasksAPI.uploadFiles(files, language, certification_type, is_urgent);
      
      dispatch({
        type: UPLOAD_TASK_SUCCESS
      });
      
      // 显示成功通知，包括消耗的点数信息
      dispatch(showNotification(`上传成功，消耗${response.data.points_required}点，剩余${response.data.points_balance}点`, 'success'));
      
      // 更新用户信息（包括最新的点数）
      dispatch({ type: 'UPDATE_USER_POINTS', payload: response.data.points_balance });
      
      // 对于每个新创建的任务，获取详细信息
      if (response.data && response.data.tasks && response.data.tasks.length > 0) {
        const createdTasks = response.data.tasks;
        
        // 异步获取每个新任务的详细信息
        const taskDetailsPromises = createdTasks.map(task => 
          tasksAPI.getTaskDetail(task.task_id)
            .then(taskDetailResponse => ({
              taskId: task.task_id,
              taskDetail: taskDetailResponse.data
            }))
            .catch(error => {
              //console.error(`获取任务 ${task.task_id} 详情失败:`, error);
              return null;
            })
        );
        
        // 等待所有任务详情获取完成
        Promise.all(taskDetailsPromises)
          .then(taskDetailResults => {
            // 过滤掉失败的请求
            const validTaskDetails = taskDetailResults.filter(result => result !== null);
            
            if (validTaskDetails.length > 0) {
              // 派发更新任务详情的action
              dispatch({
                type: 'UPDATE_TASK_DETAILS',
                payload: validTaskDetails
              });
              
              // 获取所有新任务的ID
              const newTaskIds = createdTasks.map(task => task.task_id);

              // 启动快速轮询，前两次每5秒检查一次新任务的状态
              dispatch(startTaskPolling(newTaskIds, 5000));

              // 10秒后（即2次快速查询后），切换到30秒慢速轮询
              setTimeout(() => {
                dispatch(startTaskPolling(newTaskIds, 30000));
              }, 10000); // 10秒 = 2次 × 5秒
            }
          });
      }
      
      // 返回成功结果
      return response;
    } catch (error) {
      // 首先dispatch UPLOAD_TASK_FAIL以更新Redux状态
      dispatch({ type: UPLOAD_TASK_FAIL, payload: error });
      
      // 根据不同的状态码显示不同的错误通知
      let errorMessage = '上传文件失败';
      let notificationType = 'error';
      
      if (error.response) {
        const statusCode = error.response.status;
        let responseMessage = error.response.data?.message;
        
        // 解码错误消息中的文件名
        if (responseMessage) {
          responseMessage = responseMessage.replace(
            /文件\s+([^\s]+)/g, 
            (match, encodedFileName) => {
              const decodedFileName = safeDecodeFileName(encodedFileName);
              return `文件 ${decodedFileName}`;
            }
          );
        }
        
        switch (statusCode) {
          case 401:
            // 未授权，需要登录或未上传任何文件
            errorMessage = responseMessage || '请先选择上传文件';
            break;
          case 402:
            // 文件密码保护错误
            errorMessage = responseMessage || '文件存在密码保护，请处理后再进行上传';
            break;
          case 403:
            // 点数不足
            errorMessage = responseMessage || '点数不足，请联系管理员充值';
            break;
          case 413:
            // 有效负载过大，超过文件大小或数量限制
            errorMessage = responseMessage || '文件大小或数量超过限制';
            break;
          case 415:
            // 不支持的媒体类型
            errorMessage = responseMessage || '不支持的文件格式';
            break;
          case 500:
            // 服务器内部错误
            errorMessage = responseMessage || '服务器错误，请稍后再试';
            break;
          default:
            errorMessage = responseMessage || '上传文件失败';
        }
      }
      
      // 显示错误通知
      dispatch(showNotification(errorMessage, notificationType));
      
      // 对于402错误（文件密码保护）和403错误（点数不足），不抛出错误，避免未捕获的运行时错误
      // 其他错误仍然抛出，以便调用方可以进行相应处理
      if (error.response && (error.response.status === 403 || error.response.status === 402)) {
        console.log(`🔄 [uploadFiles] ${error.response.status}错误已处理，不抛出错误避免未捕获异常`);
        return Promise.reject({ handled: true, originalError: error });
      }
      
      // 抛出错误，确保调用方进入catch块
      throw error;
    }
  };
};

// 获取任务详情
export const getTaskDetail = (taskId) => {
  return async () => {
    try {
      const response = await tasksAPI.getTaskDetail(taskId);
      return response;
    } catch (error) {
      throw error;
    }
  };
};

// 取消翻译任务
export const cancelTask = (taskId) => {
  return async (dispatch, getState) => {
    try {
      const response = await tasksAPI.cancelTask(taskId);
      
      dispatch({
        type: CANCEL_TASK_SUCCESS,
        payload: { 
          task_id: taskId,
          points_refunded: response.data.points_refunded,
          points_balance: response.data.points_balance
        }
      });
      
      // 显示成功通知，包括退回的点数信息
      dispatch(showNotification(`取消成功，已退回${response.data.points_refunded}点`, 'success'));
      
      // 更新用户点数（需要获取最新的用户信息）
      dispatch(getUserInfo());
      
      // 重新评估需要轮询的任务
      const state = getState();
      const inProgressTasks = state.tasks.tasks.filter(task => 
        (task.status === '翻译中' || task.status === '待翻译') &&
        task.task_id !== taskId // 排除刚刚取消的任务
      );
      
      // 如果还有其他任务在进行中，继续轮询这些任务
      if (inProgressTasks.length > 0) {
        const taskIdsToWatch = inProgressTasks.map(task => task.task_id);
        dispatch(startTaskPolling(taskIdsToWatch, 30000));
      } else {
        // 如果没有任务在进行中，停止轮询
        dispatch(stopTaskPolling());
      }
      
      return response;
    } catch (error) {
      // 显示错误通知
      dispatch(showNotification(error.response?.data?.message || '取消任务失败', 'error'));
      
      throw error;
    }
  };
};

// 开始轮询任务状态
export const startTaskPolling = (taskIdsToWatch = [], interval = 5000) => {
  return (dispatch, getState) => {
    // 如果已经有轮询在进行，先清除
    if (pollInterval) {
      clearInterval(pollInterval);
    }
    
    // 如果没有指定任务ID，则从当前状态获取所有进行中的任务
    const pollTasks = () => {
      const state = getState();
      let tasksToCheck = taskIdsToWatch;
      
      // 如果没有指定任务ID，则检查所有"翻译中"或"待翻译"状态的任务
      if (!tasksToCheck || tasksToCheck.length === 0) {
        const inProgressTasks = state.tasks.tasks.filter(task => 
          task.status === 'processing' || task.status === 'waiting'
        );
        tasksToCheck = inProgressTasks.map(task => task.task_id);
      }
      
      // 如果有需要查询的任务
      if (tasksToCheck.length > 0) {
        //console.log('轮询任务状态:', tasksToCheck);
        
        // 为每个任务发起查询
        tasksToCheck.forEach(taskId => {
          tasksAPI.getTaskDetail(taskId)
            .then(response => {
              if (response && response.data) {
                // 派发更新任务详情的action
                dispatch({
                  type: UPDATE_TASK_DETAILS,
                  payload: {
                    taskId: taskId,
                    taskDetail: response.data
                  }
                });
                
                // 如果任务状态是完成或失败，可以考虑发送通知
                if (response.data.status === 'completed' || response.data.status === 'failed') {
                  const message = response.data.status === 'completed' 
                    ? `任务 ${response.data.file_name} 翻译完成` 
                    : `任务 ${response.data.file_name} 翻译失败`;
                  
                  dispatch(showNotification(message, response.data.status === 'completed' ? 'success' : 'error'));
                }
              }
            })
            .catch(error => {
              //console.error(`轮询任务 ${taskId} 失败:`, error);
            });
        });
      } else {
        // 如果没有需要轮询的任务，停止轮询
        dispatch(stopTaskPolling());
      }
    };
    
    // 立即执行一次
    pollTasks();
    
    // 设置定期轮询
    pollInterval = setInterval(pollTasks, interval);
    
    // 存储interval ID以便后续可以清除
    return pollInterval;
  };
};

// 停止轮询任务状态
export const stopTaskPolling = () => {
  return () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };
};

// 删除任务文件
export const deleteTaskFiles = (taskId) => {
  return async (dispatch) => {
    dispatch({ type: DELETE_TASK_FILES_START });
    
    try {
      const response = await tasksAPI.deleteTaskFiles(taskId);
      
      dispatch({
        type: DELETE_TASK_FILES_SUCCESS,
        payload: { task_id: taskId }
      });
      
      // 显示成功通知
      dispatch(showNotification('文件删除成功', 'success'));
      
      // 获取更新后的任务详情
      const taskDetailResponse = await tasksAPI.getTaskDetail(taskId);
      if (taskDetailResponse && taskDetailResponse.data) {
        dispatch({
          type: UPDATE_TASK_DETAILS,
          payload: {
            taskId: taskId,
            taskDetail: taskDetailResponse.data
          }
        });
      }
      
      return response;
    } catch (error) {
      dispatch({
        type: DELETE_TASK_FILES_FAIL,
        payload: error.response?.data?.message || '删除文件失败'
      });
      
      // 显示错误通知
      dispatch(showNotification(error.response?.data?.message || '删除文件失败', 'error'));
      
      throw error;
    }
  };
};